import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";

// Public components
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { HomePage } from "./pages/HomePage";
import { ServicesPage } from "./pages/ServicesPage";
import { ProductsPage } from "./pages/ProductsPage";
import { ProductDetailPage } from "./pages/ProductDetailPage";
import { CartPage } from "./pages/CartPage";
import { CheckoutPage } from "./pages/CheckoutPage";
import { OrderConfirmationPage } from "./pages/OrderConfirmationPage";
import { GalleryPage } from "./pages/GalleryPage";
import { AboutPage } from "./pages/AboutPage";
import { ContactPage } from "./pages/ContactPage";
import { BookingPage } from "./pages/BookingPage";
import { BlogPage } from "./pages/BlogPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { LegalPage } from "./pages/LegalPage";
import { CGUPage } from "./pages/CGUPage";
import { ReviewPage } from "./pages/ReviewPage";

// Admin components
import { AdminLogin } from "./pages/admin/AdminLogin";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminDashboard } from "./pages/admin/AdminDashboard";
import { AdminBookings } from "./pages/admin/AdminBookings";
import { AdminServices } from "./pages/admin/AdminServices";
import { AdminProducts } from "./pages/admin/AdminProducts";
import { AdminOrders } from "./pages/admin/AdminOrders";
import { AdminMessages } from "./pages/admin/AdminMessages";
import { AdminUsers } from "./pages/admin/AdminUsers";
import { AdminBlog } from "./pages/admin/AdminBlog";
import { AdminTestimonials } from "./pages/admin/AdminTestimonials";
import { AdminGallery } from "./pages/admin/AdminGallery";
import { AdminDiscounts } from "./pages/admin/AdminDiscounts";
import { AdminCalendar } from "./pages/admin/AdminCalendar";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0EB]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A96E] border-t-transparent"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Public Layout
const PublicLayout = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
      <Route path="/produits" element={<PublicLayout><ProductsPage /></PublicLayout>} />
      <Route path="/produits/:productId" element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
      <Route path="/panier" element={<PublicLayout><CartPage /></PublicLayout>} />
      <Route path="/commande" element={<PublicLayout><CheckoutPage /></PublicLayout>} />
      <Route path="/commande/confirmation/:orderId" element={<PublicLayout><OrderConfirmationPage /></PublicLayout>} />
      <Route path="/galerie" element={<PublicLayout><GalleryPage /></PublicLayout>} />
      <Route path="/a-propos" element={<PublicLayout><AboutPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/reservation" element={<PublicLayout><BookingPage /></PublicLayout>} />
      <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
      <Route path="/blog/:id" element={<PublicLayout><BlogPostPage /></PublicLayout>} />
      <Route path="/mentions-legales" element={<PublicLayout><LegalPage /></PublicLayout>} />
      <Route path="/cgu" element={<PublicLayout><CGUPage /></PublicLayout>} />
      <Route path="/avis/:bookingId" element={<ReviewPage />} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminLayout><AdminDashboard /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/bookings" element={
        <ProtectedRoute>
          <AdminLayout><AdminBookings /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/services" element={
        <ProtectedRoute>
          <AdminLayout><AdminServices /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/products" element={
        <ProtectedRoute>
          <AdminLayout><AdminProducts /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/orders" element={
        <ProtectedRoute>
          <AdminLayout><AdminOrders /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/blog" element={
        <ProtectedRoute>
          <AdminLayout><AdminBlog /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/messages" element={
        <ProtectedRoute>
          <AdminLayout><AdminMessages /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/testimonials" element={
        <ProtectedRoute>
          <AdminLayout><AdminTestimonials /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/gallery" element={
        <ProtectedRoute>
          <AdminLayout><AdminGallery /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <AdminLayout><AdminUsers /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/discounts" element={
        <ProtectedRoute>
          <AdminLayout><AdminDiscounts /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/calendar" element={
        <ProtectedRoute>
          <AdminLayout><AdminCalendar /></AdminLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
